<template>
  <!-- The Altar's Home (altar redesign 2026-10-27, Claude outputs/altar-redesign-2026-10-27/).
       The painting IS the room; the intention sits on it like a lit candle;
       today's sky reads below. Nothing else on its face. The first visit is a
       rite: hero → today's sky writes itself in → Mystic AIda offers an
       intention from it → "Make the space yours" paints the room. -->
  <div ref="rootEl" class="alt" :class="altClass" :style="altStyle" data-testid="altar-home">
    <div class="amb"></div>
    <!-- "Make the space yours" (2026-10-30): the chosen light washes the Altar faintly. -->
    <div v-if="roomLook" class="pamb"></div>
    <AltarScenes v-if="paintStep" />
    <div ref="scroller" class="scroller" @scroll.passive="onScroll">
      <div class="room-wrap">
        <div ref="roomBox" class="room-box" :class="{ zoomable: canZoom }" :style="roomStyle" data-testid="altar-image"
             @click="openFull">
          <AltarRoom :image-url="imageUrl" :phase="roomPhase" :arrive="arriving" :reduced="reduced" :look="roomLook" :chart="chart"
                     @revealed="onRevealed" />
          <!-- Edit (AltarBuilder, with its Mystic AIda assistant) and enlarge, on the painting itself. -->
          <div v-if="canZoom" class="room-tools">
            <button type="button" class="altar-customize-cog altar-tool-disc" data-testid="altar-edit" title="Edit your altar"
                    aria-label="Edit your altar" @click.stop="openBuilder"><i class="fas fa-pencil"></i></button>
            <button type="button" class="altar-zoom altar-tool-disc" data-testid="altar-zoom" :title="C.fullSize" :aria-label="C.fullSize"
                    @click.stop="openFull"><i class="fas fa-magnifying-glass-plus"></i></button>
          </div>
        </div>
        <!-- Tablet and desktop: the caption under the preview only repeats the choices. -->
        <div v-if="step === 'paint' && look" class="recipe" aria-hidden="true">
          <div v-for="(l, i) in recipe" :key="i">{{ l }}</div>
        </div>
        <!-- Not painted yet, or it didn't take: the invite opens "Make the space
             yours" here, with the last choices kept (2026-10-30). The builder is
             one tap further, through "Shape it further". -->
        <div v-if="mode === 'home' && altarLoaded && !painted && !step" class="invite st" data-testid="altar-paint-card">
          <button type="button" @click="openPaintForm">{{ C.invite }}</button>
          <p>{{ paintFailed ? C.paintFailed : C.inviteSub }}</p>
        </div>
      </div>

      <div class="layer">
        <!-- ── The rite: today's sky ── -->
        <div v-if="mode === 'rite' && step === 'sky'" class="col rite" data-testid="rite-sky">
          <div class="rite-moon"><AltarMoon :size="isDesk ? 84 : 72" /></div>
          <div class="eyebrow">{{ eyebrowLine }}</div>
          <h2>{{ C.skyTitle }}</h2>
          <template v-if="horoState === 'reading' && horoscope">
            <div class="horo-t ln" :style="delay(0.9)">{{ horoscope.title }}</div>
            <p class="horo-b" :class="{ all: skyAll }" role="button" tabindex="0" :aria-label="C.tapAll"
               @click="showAll" @keydown.enter="showAll">
              <template v-for="(t, i) in lines" :key="i"><span class="ln" :style="delay(1.5 + i * 0.75)">{{ t }}</span>{{ i < lines.length - 1 ? ' ' : '' }}</template>
            </p>
            <div class="acts after" :style="delay(skyAll ? 0 : 1.9 + lines.length * 0.75)">
              <button type="button" class="btn" data-testid="rite-to-intent" @click="toIntent">{{ C.toIntent }}</button>
            </div>
          </template>
          <template v-else-if="horoState === 'loading'">
            <p class="pending"><span class="pulse"></span>{{ C.skyReading }}</p>
          </template>
          <template v-else>
            <!-- No birthday: a quiet line, never a box or a birthday ask. -->
            <p class="quiet-line" data-testid="rite-no-sky">{{ horoState === 'needs_birthdate' ? C.noBirthday : 'The sky is quiet just now.' }}</p>
            <div class="acts after" :style="delay(0.4)">
              <button type="button" class="btn" data-testid="rite-to-intent" @click="toIntent">{{ C.toIntent.replace(' from it', '') }}</button>
            </div>
          </template>
        </div>

        <!-- ── The intention: Mystic AIda's offer ── -->
        <div v-else-if="step === 'intent'" class="col rite" data-testid="rite-intent">
          <div class="offer"><AltarAida class="sqp" /><span>{{ horoscope ? C.offer : C.offerNoSky }}</span></div>
          <p v-if="!offerText" class="pending center"><span class="pulse"></span>{{ C.offering }}</p>
          <template v-else>
            <div id="altar-ib" ref="ibRef" :key="offerKey" class="intent-big" :class="{ swap: offerKey > 0 }" data-testid="rite-offer">
              <template v-for="(w, i) in offerWords" :key="i"><span class="w" :style="delay(0.5 + i * 0.12)">{{ w }}</span>{{ i < offerWords.length - 1 ? ' ' : '' }}</template>
            </div>
            <div class="acts after" :style="delay(offerKey > 0 ? 0.2 : 1.2 + offerWords.length * 0.12)">
              <button type="button" class="btn" data-testid="rite-keep" :disabled="busy" @click="keepOffer">{{ C.keep }}</button>
              <button type="button" class="btn ghost" :disabled="busy" @click="toOwn(offerText)">{{ C.rewrite }}</button>
              <button type="button" class="quiet" :disabled="busy" data-testid="rite-another" @click="another">{{ C.another }}</button>
            </div>
          </template>
          <p v-if="offerError" class="quiet-line">{{ offerError }}</p>
        </div>

        <!-- ── Their own words ── -->
        <div v-else-if="step === 'own'" class="col rite" data-testid="rite-own">
          <div class="offer"><span>{{ C.ownTitle }}</span></div>
          <textarea id="altar-ib" ref="ibRef" v-model="ownDraft" class="ta" :aria-label="C.ownTitle" maxlength="1000"></textarea>
          <p class="hint">{{ C.ownHint }}</p>
          <p v-if="ownError" class="err" role="alert">{{ ownError }}</p>
          <div class="acts">
            <button type="button" class="btn" :disabled="busy || !ownDraft.trim()" data-testid="rite-keep-mine" @click="keepMine">{{ C.keepMine }}</button>
            <button v-if="offerText" type="button" class="quiet" @click="step = 'intent'">{{ C.backToOffer }}</button>
            <button v-else-if="mode === 'home'" type="button" class="quiet" @click="backHome">{{ C.cancel }}</button>
          </div>
        </div>

        <!-- ── Make the space yours ── -->
        <div v-else-if="step === 'paint'" ref="paintCol" class="col rite" :class="{ waiting: landing === 'waiting', landed: landing === 'landed' }">
          <div v-if="intention" class="slot"><span ref="slotText" class="it-text"><AltarCandle :light="candleLit" />{{ intention.text }}</span></div>
          <AltarPaintForm :facets="formFacets" :chart="chart" :busy="busy"
                          :error="paintError" @paint="startPainting" @later="paintLater" @shape="openBuilder" @look="look = $event" />
        </div>

        <!-- ── Painting ── -->
        <div v-else-if="step === 'painting'" class="col rite">
          <div v-if="intention" class="slot"><span class="it-text"><AltarCandle />{{ intention.text }}</span></div>
          <div v-if="roomPhase !== 'revealing'" class="painting-msg">{{ C.painting }}<span class="d">.</span><span class="d">.</span><span class="d">.</span></div>
        </div>

        <!-- ── The everyday Altar ── -->
        <div v-else-if="mode === 'home'" class="col hm" data-testid="altar-everyday">
          <div class="date st" :style="st(0)">{{ eyebrowLine }}</div>
          <div v-if="intention" class="intent-wrap">
            <button ref="intentBtn" type="button" class="intent st" :style="st(1)" data-testid="altar-intention"
                    :aria-label="`Your intention: ${intention.text}. Hold or renew`" @click="sheet = 'intent'">
              <span class="row"><span ref="homeIntentText" class="it-text"><AltarCandle :light="candleLit" />{{ intention.text }}</span></span>
              <span class="since">{{ since }} ›</span>
            </button>
            <!-- The affirmation's own pencil (PersonalAffirmation's): it opens the
                 Mystic AIda chat scoped to the affirmation, with its Magic / Spoken
                 mode bar. A second tap closes it. -->
            <button type="button" class="intent-edit st" :style="st(1)" data-testid="altar-intent-edit"
                    aria-label="Edit intention" title="Edit intention" @click="openIntentChat">
              <i class="fas fa-pencil"></i>
            </button>
          </div>
          <button v-else-if="intentionLoaded" type="button" class="intent st empty" :style="st(1)" data-testid="altar-intention-empty" @click="openIntentChat">
            <span class="since">✦ Set an intention from today’s sky ›</span>
          </button>
          <section ref="horoSection" class="horo st" :style="st(2)" aria-label="Your horoscope">
            <button v-if="horoState === 'reading' && horoscope" type="button" class="horo-edit" :class="{ on: horoEditing }"
                    data-testid="altar-horo-edit" aria-label="Edit horoscope" title="Edit horoscope" @click="toggleHoroEdit">
              <i class="fas fa-pencil"></i>
            </button>
            <div class="rule"></div>
            <div class="eyebrow">{{ C.skyEyebrow }}<i>·</i>{{ C.skyEyebrowWhen }}</div>
            <HoroscopeTuneForm v-if="horoEditing" v-model:focus="tuneFocus" v-model:words="tuneWords" class="horo-tune"
                               :busy="busy" @go="doTune" @cancel="closeHoroEdit" />
            <template v-else-if="horoState === 'reading' && horoscope">
              <div class="horo-t">{{ horoscope.title }}</div>
              <p class="horo-b" data-testid="altar-horoscope">{{ horoscope.reading_text }}</p>
            </template>
            <p v-else-if="horoState === 'loading'" class="pending"><span class="pulse"></span>{{ C.skyReading }}</p>
            <p v-else-if="horoState === 'needs_birthdate'" class="quiet-line" data-testid="altar-no-sky">
              Your sky arrives here once your birthday is with us.
              <button v-if="hasMirror" type="button" class="inline-link" @click="$emit('open-mirror')">Your Mirror holds it ›</button>
            </p>
            <p v-else class="quiet-line">The sky is quiet just now. <button type="button" class="inline-link" @click="loadHoroscope">Read it again ›</button></p>
            <p v-if="riteJustDone && horoState === 'reading'" class="closing">{{ C.closing }}</p>
          </section>
        </div>
      </div>
    </div>

    <!-- ── The hero (first visit) ── -->
    <div v-if="mode === 'rite' && step === 'hero'" class="ovl">
      <div class="hero" role="dialog" aria-modal="true" :aria-label="C.heroTitle" data-testid="rite-hero">
        <div class="art"><AltarMoon :size="72" /></div>
        <div class="eyebrow">{{ C.heroEyebrow }}</div>
        <h3>{{ C.heroTitle }}</h3>
        <p>{{ C.heroP1 }}</p>
        <p>{{ C.heroP2 }}</p>
        <p class="q">{{ C.heroQuiet }}</p>
        <button ref="startBtn" type="button" class="btn" data-testid="rite-start" @click="start">{{ C.getStarted }}</button>
      </div>
    </div>

    <!-- ── Hold or renew (tap the intention) ── -->
    <template v-if="sheet === 'intent' && intention">
      <div class="scrim" @click="sheet = ''"></div>
      <div class="sheet" role="dialog" aria-modal="true" aria-label="Your intention" data-testid="altar-intent-sheet">
        <div class="grab"></div>
        <div class="eyebrow">{{ C.sheetEyebrow }}</div>
        <p class="it-text">{{ intention.text }}</p>
        <p class="sub">{{ since.replace('Your intention, ', '').replace(/^./, (c) => c.toUpperCase()) }}.</p>
        <button type="button" class="btn" @click="sheet = ''">{{ C.holdOn }}</button>
        <button type="button" class="btn ghost" style="margin-top:10px" data-testid="altar-renew" @click="renew">{{ C.renew }}</button>
        <button type="button" class="quiet" @click="writeOwn">{{ C.writeOwn }}</button>
      </div>
    </template>

    <!-- ── Full size (the pencil's neighbour, or a click on the painting) ── -->
    <div v-if="sheet === 'full' && imageUrl" class="lbx" data-testid="altar-lightbox" @click="sheet = ''">
      <img :src="imageUrl" class="lbx-img" alt="Your Altar" @click.stop />
      <button type="button" class="x" aria-label="Close" @click="sheet = ''">×</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { store } from '@/store'
import { useAltarToday } from '@/composables/useAltarToday'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { RITE_COPY as C, skyLines, dateEyebrow, heldSince, riteDue, localToday, moodLook, paintRecipe } from '@/utils/altar-rite'
import AltarRoom from '@/components/altar/AltarRoom.vue'
import AltarScenes from '@/components/altar/AltarScenes.vue'
import AltarCandle from '@/components/altar/AltarCandle.vue'
import AltarPaintForm from '@/components/altar/AltarPaintForm.vue'
import AltarMoon from '@/components/altar/AltarMoon.vue'
import AltarAida from '@/components/altar/AltarAida.vue'
import HoroscopeTuneForm from '@/components/ui/HoroscopeTuneForm.vue'

const props = defineProps({
  active: { type: Boolean, default: true },     // the Home tab is the one showing
  hasMirror: { type: Boolean, default: false },
})
const emit = defineEmits(['rite', 'open-builder', 'open-mirror', 'scrolled'])

const reduced = useReducedMotion()
const {
  horoState, horoscope, sky, chart, intention, intentionLoaded, altar, altarLoaded, imageUrl, painted,
  loadHoroscope, loadIntention, loadAll, offerIntention, keepOwnWords, retune, paint, riteDone, markOpened, setAltarImage,
  setHoroscope,
} = useAltarToday()

// ── mode ───────────────────────────────────────────────────────────────────
const mode = ref('home')        // rite | home
const step = ref('')            // hero | sky | intent | own | paint | painting | '' (home)
const renewing = ref(false)     // the intention steps opened from the everyday Altar
const sheet = ref('')           // intent | full
const busy = ref(false)
const riteJustDone = ref(false)
const paintFailed = ref(false)
const arriving = ref(true)      // eyes adjusting, once per visit
const assemble = ref(false)
const settled = ref(false)
const candleLit = ref(false)
const landing = ref('')         // '' | waiting | landed (the kept words rising into place)

watch(() => mode.value === 'rite', (on) => emit('rite', on), { immediate: true })

// ── layout ─────────────────────────────────────────────────────────────────
const rootEl = ref(null)
const scroller = ref(null)
const roomBox = ref(null)
const roomH = ref(420)
const isDesk = ref(false)
const isWide = ref(false)        // two columns while painting: tablet (760px) and up
const scrollY = ref(0)
let ro = null
function measure () {
  if (roomBox.value) roomH.value = roomBox.value.offsetHeight || roomH.value
  // Two columns when the Altar itself is wide enough (its container query
  // below): the assistant's pane narrows the panel, and the Altar folds to
  // one column beside it rather than crushing the words.
  const w = rootEl.value && rootEl.value.offsetWidth
  const desk = w ? w >= 1000 : !!(window.matchMedia && window.matchMedia('(min-width: 1000px)').matches)
  const flipped = desk !== isDesk.value
  isDesk.value = desk
  isWide.value = w ? w >= 760 : !!(window.matchMedia && window.matchMedia('(min-width: 760px)').matches)
  if (flipped && horoEditing.value) nextTick(showHoroForm)
}
function onScroll () { scrollY.value = scroller.value ? scroller.value.scrollTop : 0 }
// Off the top, the Altar's tab strip turns into a band so the words never print under it.
watch(() => scrollY.value > 4, (on) => emit('scrolled', on))
// On a phone the sky slides up over the painting, which dims.
const roomStyle = computed(() => {
  if (isDesk.value) return {}
  // "Make the space yours" on a phone: the lit room sits at the top, behind the
  // intention, and dims as the choices scroll over it (2026-10-30).
  if (step.value === 'paint') return isWide.value ? {} : { opacity: String(Math.max(0.3, 1 - scrollY.value / 380)) }
  if (mode.value !== 'home') return {}
  const y = scrollY.value
  return { transform: reduced.value ? '' : `translateY(${(-y * 0.35).toFixed(1)}px)`, opacity: String(Math.max(0.2, 1 - y / 340)) }
})

const altClass = computed(() => ({
  rite: mode.value === 'rite', home: mode.value === 'home', lit: painted.value && roomPhase.value === 'painted',
  assemble: assemble.value, settled: settled.value, rm: reduced.value, desk: isDesk.value, 'paint-step': paintStep.value,
  choosing: step.value === 'paint',
}))

// ── "Make the space yours": the room is the preview (2026-10-30) ─────────────
const paintStep = computed(() => step.value === 'paint' || step.value === 'painting')
const look = ref(null)          // the form's { mood, style, paths }
const roomLook = computed(() => (paintStep.value ? look.value : null))
const recipe = computed(() => paintRecipe(look.value || {}))
// The last choices sent to paint: kept when it didn't take or carats ran short.
const lastFacets = ref(null)
const formFacets = computed(() => lastFacets.value || (altar.value && altar.value.facets) || null)
const altStyle = computed(() => {
  const o = { '--room-h': roomH.value + 'px' }
  if (roomLook.value) {
    const l = moodLook(roomLook.value.mood)
    Object.assign(o, { '--m1': l[1], '--m2': l[2], '--m3': l[3], '--m4': l[4] })
  }
  return o
})

// ── the room ───────────────────────────────────────────────────────────────
const drawPhase = ref('')       // drawing | shimmer | revealing | '' while painting
const roomPhase = computed(() => {
  if (drawPhase.value) return drawPhase.value
  if (mode.value === 'rite' || step.value === 'paint') return 'dark'
  return painted.value ? 'painted' : 'dark'
})

// ── the sky ────────────────────────────────────────────────────────────────
const lines = computed(() => skyLines(horoscope.value && horoscope.value.reading_text))
const skyAll = ref(false)
const eyebrowLine = computed(() => {
  const d = dateEyebrow((horoscope.value && horoscope.value.date) || localToday())
  const s = sky.value && sky.value.line
  return s ? `${d} · ${s}` : d
})
function showAll () { skyAll.value = true }
const since = computed(() => heldSince(intention.value && intention.value.created_at))
const delay = (s) => ({ animationDelay: (reduced.value ? 0 : s).toFixed(2) + 's' })
const st = (i) => ({ animationDelay: (settled.value ? 0 : (assemble.value ? 0.2 : (arriving.value && !reduced.value ? 1.0 : 0.2)) + i * 0.45) + 's' })

// The horoscope counts as read once it is on screen (journal API §6).
watch([() => mode.value, () => step.value, () => horoState.value, () => props.active], () => {
  if (!props.active || horoState.value !== 'reading') return
  if ((mode.value === 'rite' && step.value === 'sky') || (mode.value === 'home' && !step.value)) markOpened()
})

// ── the rite ───────────────────────────────────────────────────────────────
const startBtn = ref(null)
let riteChecked = false
function maybeStartRite () {
  if (riteChecked || !altarLoaded.value) return
  riteChecked = true
  if (riteDue(altar.value, { signedIn: !!store.querent_id, onHome: props.active })) {
    mode.value = 'rite'
    step.value = 'hero'
    arriving.value = false
    nextTick(() => { if (startBtn.value) startBtn.value.focus() })
  }
}
watch([altarLoaded, () => props.active], maybeStartRite)

function start () {
  // The welcome is seen once: recorded now, so leaving at the sky, the
  // intention or the painting lands on the everyday Altar next time, which
  // holds the rest (set an intention, paint your Altar). Prod 2026-10-30.
  riteDone({ announce: false })
  step.value = 'sky'
  skyAll.value = reduced.value
  scrollTop()
}
function scrollTop () { nextTick(() => { if (scroller.value) scroller.value.scrollTop = 0 }) }

// ── the intention ──────────────────────────────────────────────────────────
const offerText = ref('')
const offerKey = ref(0)
const offerError = ref('')
const offerWords = computed(() => offerText.value.split(/\s+/).filter(Boolean))
const ownDraft = ref('')
const ownError = ref('')
const ibRef = ref(null)

async function fetchOffer () {
  offerError.value = ''
  busy.value = true
  try {
    const a = await offerIntention()
    if (a && a.text) offerText.value = a.text
    else offerError.value = 'Mystic AIda is quiet just now. Write your own, or try again.'
  } catch (e) {
    offerError.value = 'Mystic AIda is quiet just now. Write your own, or try again.'
  } finally { busy.value = false }
}
function toIntent () {
  // An intention already held (set from a Quest sheet, say) is kept: on to the painting.
  if (mode.value === 'rite' && intention.value && !renewing.value) { afterIntention(); return }
  step.value = 'intent'
  scrollTop()
  if (!offerText.value) fetchOffer()
}
async function another () {
  offerKey.value++
  await fetchOffer()
}
function toOwn (text) {
  ownDraft.value = text || ''
  ownError.value = ''
  step.value = 'own'
  nextTick(() => { if (ibRef.value && ibRef.value.focus) ibRef.value.focus() })
}
function keepOffer () { flyThen(afterIntention) }
async function keepMine () {
  const text = ownDraft.value.trim()
  if (!text || busy.value) return
  busy.value = true
  ownError.value = ''
  try {
    const r = await keepOwnWords(text)
    if (!r.ok) {
      ownError.value = r.rejected ? 'Those words couldn’t be kept as they are. Try them another way.' : 'They didn’t save just now. Try again.'
      return
    }
    flyThen(afterIntention)
  } catch (e) {
    ownError.value = 'They didn’t save just now. Try again.'
  } finally { busy.value = false }
}
function afterIntention () {
  offerText.value = ''
  offerKey.value = 0
  if (renewing.value) { backHome(); lightCandle(); return }
  if (painted.value) { finishRite(false); return }
  step.value = 'paint'
  scrollTop()
}

// The kept words rise into their place and a candle lights. The next screen
// waits until they land. Reduced motion: no rising, a crossfade.
function flyThen (next) {
  const src = ibRef.value
  if (reduced.value || !src || !src.getBoundingClientRect || !scroller.value) { next(); lightCandle(); return }
  const host = scroller.value.parentElement
  const hostR = host.getBoundingClientRect()
  const a = src.getBoundingClientRect()
  const clone = document.createElement('div')
  clone.className = 'altar-fly'
  clone.textContent = src.tagName === 'TEXTAREA' ? src.value : src.textContent
  Object.assign(clone.style, { left: (a.left - hostR.left) + 'px', top: (a.top - hostR.top) + 'px', width: a.width + 'px',
    textAlign: getComputedStyle(src).textAlign })
  host.appendChild(clone)
  next()
  nextTick(() => {
    const target = slotText.value || homeIntentText.value
    if (!target || !clone.animate) { clone.remove(); lightCandle(); return }
    target.style.opacity = '0'
    if (step.value === 'paint') landing.value = 'waiting'
    const b = target.getBoundingClientRect()
    const fs = parseFloat(getComputedStyle(target).fontSize) / parseFloat(getComputedStyle(clone).fontSize)
    const anim = clone.animate([{ transform: 'none', opacity: 1 },
      { transform: `translate(${b.left - a.left}px, ${b.top - a.top}px) scale(${fs})`, opacity: 0.9 }],
    { duration: 950, easing: 'cubic-bezier(.45,.05,.2,1)', fill: 'forwards' })
    anim.onfinish = () => {
      clone.remove()
      target.style.opacity = ''
      lightCandle()
      setTimeout(() => { if (landing.value === 'waiting') landing.value = 'landed' }, 700)
    }
  })
}
const slotText = ref(null)
const homeIntentText = ref(null)
function lightCandle () {
  candleLit.value = false
  nextTick(() => { candleLit.value = true })
}

// ── the painting ───────────────────────────────────────────────────────────
const paintError = ref('')
let paintResult = null
let outlineDrawn = false
let timers = []
const later = (ms, fn) => timers.push(setTimeout(fn, ms))
function clearTimers () { timers.forEach(clearTimeout); timers = [] }

async function startPainting (facets) {
  lastFacets.value = facets
  paintError.value = ''
  busy.value = true
  step.value = 'painting'
  scrollTop()
  drawPhase.value = 'drawing'
  paintResult = null
  outlineDrawn = false
  later(reduced.value ? 300 : 2400, () => {
    outlineDrawn = true
    if (paintResult) settlePainting()
    else drawPhase.value = 'shimmer'
  })
  const r = await paint(facets)
  // Load the image before the colour spreads, so the reveal is the real picture.
  if (r.ok && r.url) await new Promise((res) => { const im = new Image(); im.onload = res; im.onerror = res; im.src = r.url })
  paintResult = r
  if (outlineDrawn) settlePainting()
}
function settlePainting () {
  const r = paintResult
  busy.value = false
  if (r && r.ok) { drawPhase.value = 'revealing'; return }
  drawPhase.value = ''
  if (r && r.reason === 'insufficient_carats') {
    paintError.value = r.words || 'Your place has used what it came with for now. Quest shows what opens next.'
    step.value = 'paint'
    // The words sit above the gold button, at the foot of the form.
    nextTick(() => { if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight })
    return
  }
  paintFailed.value = true
  if (mode.value === 'rite') finishRite(false)
  else backHome()
}
function onRevealed () {
  drawPhase.value = ''
  paintFailed.value = false
  lastFacets.value = null
  if (mode.value === 'rite') finishRite(true)
  else { backHome(); assemble.value = true }
}
function paintLater () {
  if (mode.value === 'rite') finishRite(false)
  else backHome()
}

function finishRite (justPainted) {
  riteDone()
  riteJustDone.value = true
  mode.value = 'home'
  step.value = ''
  arriving.value = !justPainted
  assemble.value = !!justPainted
  scrollTop()
}
function backHome () {
  renewing.value = false
  mode.value = mode.value === 'rite' ? 'rite' : 'home'
  step.value = ''
  sheet.value = ''
  settled.value = true
  arriving.value = false
  scrollTop()
}

// ── the everyday tools ─────────────────────────────────────────────────────
function renew () {
  sheet.value = ''
  renewing.value = true
  offerText.value = ''
  offerKey.value = 0
  step.value = 'intent'
  scrollTop()
  fetchOffer()
}
function writeOwn () {
  sheet.value = ''
  renewing.value = true
  offerText.value = ''
  toOwn(intention.value ? intention.value.text : '')
}
// Edit / repaint: Chris's AltarBuilder (MysticAltar hosts it), with its
// Advanced description and the Mystic AIda assistant. The horoscope's form
// and its assistant close first, as they always did.
function openBuilder () {
  sheet.value = ''
  if (horoEditing.value) closeHoroEdit()
  emit('open-builder')
}
// Not painted yet (or it didn't take): the invite opens "Make the space yours"
// in the room, the last choices kept. "Paint it later" goes back home.
function openPaintForm () {
  sheet.value = ''
  if (horoEditing.value) closeHoroEdit()
  renewing.value = false
  paintError.value = ''
  mode.value = 'home'
  step.value = 'paint'
  scrollTop()
}
// The painting, full size: the magnifier or a click on the painting.
const canZoom = computed(() => mode.value === 'home' && !step.value && painted.value && roomPhase.value === 'painted')
function openFull () { if (canZoom.value) sheet.value = 'full' }

// The horoscope's own pencil (PersonalHoroscope's): the tune form replaces the
// reading and the Mystic AIda assistant opens on the horoscope.
const horoEditing = ref(false)
const tuneFocus = ref('')
const tuneWords = ref(60)
function toggleHoroEdit () {
  if (horoEditing.value) { closeHoroEdit(); return }
  tuneFocus.value = (horoscope.value && horoscope.value.focus) || ''
  horoEditing.value = true
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: true, scope: 'horoscope' } }))
  nextTick(showHoroForm)
}
const horoSection = ref(null)
function showHoroForm () {
  const el = horoSection.value
  if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: reduced.value ? 'auto' : 'smooth' })
}
function closeHoroEdit () {
  horoEditing.value = false
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: false } }))
}
async function doTune () {
  if (busy.value) return
  busy.value = true
  try { await retune(tuneFocus.value, tuneWords.value) } catch (e) { /* */ } finally { busy.value = false }
  if (horoEditing.value) closeHoroEdit()
}

// The intention's pencil, and the empty line's "Set an intention": the old
// PersonalAffirmation pencil and "Set intention" button, unchanged. The
// chat (ReaderPage, scope 'affirmation') saves through customizePersonalAffirmation
// (Magic) or setPersonalAffirmation (Spoken) and fires affirmation-updated, which
// reloads the intention below. The horoscope form gives way to it, as the
// affirmation card was hidden while the horoscope was edited.
function openIntentChat () {
  sheet.value = ''
  horoEditing.value = false
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { toggle: true, scope: 'affirmation' } }))
}

// The Quest's "Set your intention" / "Paint your Altar" open these same steps.
function openIntent () { if (mode.value === 'home') renew() }
defineExpose({ openIntent, openPaint: openPaintForm, mode, step })

// ── outside events ─────────────────────────────────────────────────────────
function onImageUpdated (e) {
  const d = (e && e.detail) || {}
  if ((d.subject || 'altar') !== 'altar' || !d.url) return
  setAltarImage(d.url)
  // Painted in the builder from the rite's paint step: the rite is complete.
  if (mode.value === 'rite' && step.value === 'paint') finishRite(true)
}
// The assistant reshaped the horoscope from chat: show it, close the form.
function onHoroscopeUpdated (e) {
  const h = e && e.detail && e.detail.horoscope
  if (!h) return
  setHoroscope(h)
  if (horoEditing.value) closeHoroEdit()
}
function onAffirmationUpdated (e) { if (e && e.detail && e.detail.affirmation) loadIntention() }
function onKey (e) { if (e.key === 'Escape' && sheet.value) sheet.value = '' }

onMounted(() => {
  watch(() => store.session_id, (id) => { if (id) loadAll() }, { immediate: true })
  watch(() => store.querent_birthdate, (bd, was) => { if (bd && !was) loadHoroscope() })
  window.addEventListener('altar-image-updated', onImageUpdated)
  window.addEventListener('horoscope-updated', onHoroscopeUpdated)
  window.addEventListener('affirmation-updated', onAffirmationUpdated)
  window.addEventListener('keydown', onKey)
  window.addEventListener('resize', measure)
  if (window.ResizeObserver && roomBox.value) {
    ro = new ResizeObserver(measure)
    ro.observe(roomBox.value)
    if (rootEl.value) ro.observe(rootEl.value)
  }
  measure()
  later(2600, () => { arriving.value = false })
})
onBeforeUnmount(() => {
  clearTimers()
  window.removeEventListener('altar-image-updated', onImageUpdated)
  window.removeEventListener('horoscope-updated', onHoroscopeUpdated)
  window.removeEventListener('affirmation-updated', onAffirmationUpdated)
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('resize', measure)
  if (ro) { ro.disconnect(); ro = null }
})
</script>

<style scoped>
.alt { position: absolute; inset: 0; overflow: hidden; color: #efe4ff; container-type: inline-size;
  background: radial-gradient(130% 55% at 50% 0%, #1f103e 0%, #0d0520 62%);
  --gold: #c9a94e; --goldlt: #f0d9a3; --goldhi: #ffe7b5; --mut: #b9a6d6; --dim: #8e7bb5; --lilac: #9b86c9; --ink2: #e6dafa;
  --serif: 'Cormorant Garamond', 'Cormorant', Georgia, serif; }
.scroller { position: absolute; inset: 0; overflow-y: auto; overflow-x: hidden; scrollbar-width: none; }
.scroller::-webkit-scrollbar { display: none; }
.alt { transition: --m1 .9s ease, --m2 .9s ease, --m3 .9s ease, --m4 .9s ease; }
/* The chosen light, washing the whole Altar faintly while the form is open. */
.pamb { position: absolute; inset: 0; pointer-events: none; animation: alt-fade .8s ease both;
  background: radial-gradient(90% 46% at 50% 18%, color-mix(in srgb, var(--m3) 15%, transparent), transparent 72%),
    radial-gradient(120% 60% at 50% 100%, color-mix(in srgb, var(--m1) 50%, transparent), transparent 70%); }
.recipe { display: none; }
.amb { position: absolute; inset: 0; opacity: 0; transition: opacity 1.6s ease; pointer-events: none;
  background: radial-gradient(42% 60% at 30% 50%, rgba(233,166,90,.20), transparent 70%), radial-gradient(60% 70% at 26% 40%, rgba(44,79,138,.42), transparent 75%), radial-gradient(90% 90% at 80% 10%, rgba(46,24,87,.6), transparent 70%); }

/* The room stays put while the page scrolls over it. */
.room-wrap { position: sticky; top: 0; height: 0; z-index: 0; }
/* The whole painting (square, never cropped), dissolving evenly into the
   room on every side: a smoothstep feather on all four edges (flat at both
   ends, so no ridge or line shows where it starts or finishes). */
.room-box { position: absolute; left: 0; right: 0; top: -6px; aspect-ratio: 1 / 1; max-height: 82vh; margin: 0 auto; max-width: 640px; will-change: transform, opacity;
  --fe-x: linear-gradient(to right, transparent 0%, rgba(0,0,0,.055) 2%, rgba(0,0,0,.19) 4%, rgba(0,0,0,.37) 6%, rgba(0,0,0,.63) 8%, rgba(0,0,0,.81) 10%, rgba(0,0,0,.945) 12%, #000 14%, #000 86%, rgba(0,0,0,.945) 88%, rgba(0,0,0,.81) 90%, rgba(0,0,0,.63) 92%, rgba(0,0,0,.37) 94%, rgba(0,0,0,.19) 96%, rgba(0,0,0,.055) 98%, transparent 100%);
  --fe-y: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,.055) 2%, rgba(0,0,0,.19) 4%, rgba(0,0,0,.37) 6%, rgba(0,0,0,.63) 8%, rgba(0,0,0,.81) 10%, rgba(0,0,0,.945) 12%, #000 14%, #000 86%, rgba(0,0,0,.945) 88%, rgba(0,0,0,.81) 90%, rgba(0,0,0,.63) 92%, rgba(0,0,0,.37) 94%, rgba(0,0,0,.19) 96%, rgba(0,0,0,.055) 98%, transparent 100%);
  -webkit-mask-image: var(--fe-x), var(--fe-y); -webkit-mask-composite: source-in;
  mask-image: var(--fe-x), var(--fe-y); mask-composite: intersect; }
.room-box.zoomable { cursor: zoom-in; }
/* The pencil (AltarBuilder) and the magnifier sit on the painting's lower
   right, inside the feather. On a phone the words cover the lower painting,
   so they sit just above the date line there. */
.room-tools { position: absolute; z-index: 2; right: 15%; top: calc(60% - 40px); display: flex; gap: 4px; opacity: .55; transition: opacity .25s; }
.room-box:hover .room-tools, .room-tools:focus-within { opacity: 1; }
/* Chris 2026-10-28: discreet and out of the way. A small, faint glass disc
   that comes up on hover or focus; 28px, with a 44px hit area. */
.altar-tool-disc { position: relative; width: 28px; height: 28px; border-radius: 50%; padding: 0; background: rgba(20,8,36,.45); border: 1px solid rgba(240,217,163,.28); color: rgba(246,231,184,.85); box-shadow: none; font-size: 11px; display: flex; align-items: center; justify-content: center; cursor: pointer; backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px); transition: background .2s, border-color .2s, color .2s; }
.altar-tool-disc::after { content: ''; position: absolute; inset: -8px; border-radius: 50%; }
.altar-tool-disc:hover, .altar-tool-disc:focus-visible { background: rgba(36,16,58,.85); border-color: rgba(240,217,163,.7); color: #f6e7b8; }
@media (hover: none) { .room-tools { opacity: .75; } }
.invite { position: absolute; z-index: 6; left: 0; right: 0; top: calc(var(--room-h) * .34); text-align: center; }
.invite button { border: 1px solid rgba(240,217,163,.6); background: rgba(13,5,32,.55); color: var(--goldlt); border-radius: 999px; padding: 10px 22px;
  font: 600 12.5px 'Cinzel', serif; letter-spacing: .12em; text-transform: uppercase; box-shadow: 0 0 30px rgba(240,217,163,.15); cursor: pointer; }
.invite p { font: italic 17px var(--serif); color: var(--mut); margin: 10px 24px 0; }

/* The words sit over the room; empty space lets taps through to the ⋯ and the invitation beneath. */
.layer { position: relative; z-index: 5; padding: 0 22px; pointer-events: none; }
.layer .col > * { pointer-events: auto; }
.col { max-width: 420px; margin: 0 auto; position: relative; }
/* The bar is fixed over the panel's foot (65px + the safe area): the last line
   ("Shape it further…") scrolls clear of it (prod 2026-10-30). */
.col.rite { padding-top: var(--tra-clear-top, 60px); text-align: center; padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)); }
/* Phone: the lit room sits under the intention and shows above the title. */
.alt.paint-step .col { --mk-head-top: max(22px, calc(var(--room-h) * .74 - 66px)); }
.alt.choosing .room-box { top: 70px; }
.alt:not(.rm) .room-box { transition: top .6s ease; }

/* shared type */
.eyebrow { font: 600 10px 'Cinzel', serif; letter-spacing: .2em; text-transform: uppercase; color: var(--goldlt); }
.eyebrow i { font-style: normal; color: var(--dim); margin: 0 6px; }
.btn { display: block; width: 100%; border: 0; border-radius: 14px; padding: 12px 14px; background: linear-gradient(#f3dfa8, #c9a94e); color: #22133b;
  font: 600 13px 'Cinzel', serif; letter-spacing: .1em; text-transform: uppercase; box-shadow: 0 6px 18px rgba(201,169,78,.25); cursor: pointer; }
.btn:disabled { opacity: .55; cursor: default; }
.btn.ghost { background: rgba(13,5,32,.35); color: var(--goldlt); border: 1px solid rgba(240,217,163,.55); box-shadow: none; }
.btn + .btn { margin-top: 10px; }
.quiet { display: block; margin: 10px auto 0; background: none; border: 0; color: var(--mut); font: italic 17px var(--serif); text-decoration: underline;
  text-underline-offset: 3px; cursor: pointer; }
.quiet:hover { color: var(--goldlt); }
.inline-link { background: none; border: 0; padding: 0; color: var(--goldlt); font: inherit; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
button:focus-visible, .ta:focus-visible, .horo-b:focus-visible { outline: 2px solid var(--goldhi); outline-offset: 2px; }
.quiet-line { font: italic 18px/1.45 var(--serif); color: var(--mut); margin: 16px 0 0; }
.pending { display: flex; align-items: center; gap: 10px; font: italic 18px var(--serif); color: var(--lilac); margin: 18px 0 0; }
.pending.center { justify-content: center; }
.pulse { width: 8px; height: 8px; border-radius: 50%; background: var(--goldlt); animation: alt-pulse 1.3s ease-in-out infinite; }
.err { font: italic 16px var(--serif); color: #f0c4a3; margin: 8px 2px 0; }

/* the hero */
.ovl { position: absolute; inset: 0; z-index: 40; display: flex; align-items: center; justify-content: center; background: rgba(7,2,15,.55); }
.hero { width: calc(100% - 36px); max-width: 350px; text-align: center; padding: 20px 22px 18px; border-radius: 24px;
  background: radial-gradient(120% 80% at 50% 0%, #2e1857, #150a2b 75%); border: 1px solid rgba(240,217,163,.55);
  box-shadow: 0 0 60px rgba(240,217,163,.16); animation: alt-rise .6s cubic-bezier(.2,.8,.2,1) both; }
.hero .art { display: flex; justify-content: center; margin-bottom: 8px; }
.hero h3 { font: 500 31px/1.08 var(--serif); margin: 6px 0 8px; color: #fff; }
.hero p { color: var(--ink2); margin: 0 0 9px; font-size: 14px; line-height: 1.5; text-align: left; }
.hero p.q { font: italic 16.5px/1.35 var(--serif); color: var(--mut); border-top: 1px solid rgba(155,134,201,.2); padding-top: 9px; }
.hero .btn { margin-top: 12px; }

/* the sky */
.rite-moon { display: flex; justify-content: center; margin: 26px 0 6px; }
.rite-moon :deep(svg) { animation: alt-moonin 1.6s ease-out both; }
.rite .eyebrow { animation: alt-fade .8s .1s ease both; }
.rite h2 { font: 500 32px/1.08 var(--serif); color: #fff; margin: 8px 0 2px; animation: alt-up .9s .3s ease both; }
.horo-t { font: italic 500 24px/1.2 var(--serif); color: var(--goldlt); margin: 14px 0 8px; }
.horo-b { font: 19px/1.5 var(--serif); color: var(--ink2); margin: 0; text-align: left; }
.rite .horo-b { cursor: pointer; }
.ln { opacity: 0; animation: alt-lnin .9s ease forwards; }
.horo-b.all .ln { animation-delay: 0s !important; animation-duration: .25s; }
.after { opacity: 0; animation: alt-up .7s ease forwards; }
.rite .acts { margin: 22px 0 28px; }

/* the intention */
.offer { display: flex; align-items: center; gap: 10px; justify-content: center; margin: 34px 0 6px; color: var(--mut); font: italic 18px var(--serif);
  animation: alt-fade .8s ease both; }
.offer .sqp { width: 30px; height: 30px; flex: none; }
.intent-big { font: italic 500 33px/1.18 var(--serif); color: var(--goldhi); text-shadow: 0 0 22px rgba(240,217,163,.35); margin: 14px 0 26px; }
.intent-big .w { display: inline-block; opacity: 0; animation: alt-wordin .9s ease forwards; }
.intent-big.swap { animation: alt-fade .5s ease both; }
.ta { width: 100%; box-sizing: border-box; min-height: 96px; resize: vertical; border-radius: 14px; border: 1px solid rgba(240,217,163,.4);
  background: rgba(13,5,32,.6); color: var(--goldhi); font: italic 24px/1.3 var(--serif); padding: 12px 14px; }
.ta:focus { outline: none; border-color: var(--goldlt); }
.hint { font: italic 15px var(--serif); color: var(--mut); margin: 8px 2px 0; text-align: left; }
.slot { text-align: center; margin: 20px 0 4px; }
.it-text { font: italic 500 21px/1.25 var(--serif); color: var(--goldhi); text-shadow: 0 0 14px rgba(240,217,163,.3); transition: opacity .2s; }
.col.waiting > :not(.slot) { opacity: 0; }
.col.landed > :not(.slot) { animation: alt-fade .9s ease both; }

/* painting */
.painting-msg { margin-top: calc(var(--room-h) * .62); text-align: center; font: italic 21px var(--serif); color: var(--goldlt); animation: alt-fade 1s ease both; }
.painting-msg .d { display: inline-block; animation: alt-dots 1.4s infinite; }
.painting-msg .d:nth-child(2) { animation-delay: .2s; } .painting-msg .d:nth-child(3) { animation-delay: .4s; }

/* the everyday Altar */
.hm { padding-top: calc(var(--room-h) * .6); padding-bottom: 30px; text-align: center; }
.hm .date { font: 600 10px 'Cinzel', serif; letter-spacing: .2em; text-transform: uppercase; color: var(--goldlt); text-shadow: 0 1px 8px #000; }
.intent { display: block; width: 100%; text-align: inherit; background: none; border: 0; padding: 6px 0 4px; margin: 6px 0 0; border-radius: 12px; cursor: pointer; color: inherit; }
.intent .row { display: block; }
/* The intention's pencil (PersonalAffirmation's ghost pencil), at its top right. */
.intent-wrap { position: relative; }
.intent-wrap .intent { padding-left: 30px; padding-right: 30px; }
.intent-edit { position: absolute; top: 6px; right: -4px; width: 30px; height: 30px; border: 0; background: none; color: var(--lilac);
  font-size: 13px; cursor: pointer; text-shadow: 0 1px 8px #000; transition: color .2s; }
.intent-wrap:hover .intent-edit, .intent-edit:hover, .intent-edit:focus-visible { color: var(--goldlt); }
.intent .it-text { font-size: 25px; line-height: 1.22; text-shadow: 0 0 18px rgba(240,217,163,.35), 0 1px 10px #000; }
.intent .since { display: block; font: italic 15px var(--serif); color: var(--mut); margin-top: 3px; text-shadow: 0 1px 8px #000; }
.intent:hover .since { color: var(--goldlt); }
.intent:hover .it-text { text-decoration: underline; text-decoration-color: rgba(240,217,163,.35); text-underline-offset: 5px; text-decoration-thickness: 1px; }
.intent.empty .since { font-size: 18px; color: var(--goldlt); }
.horo { position: relative; margin-top: 26px; text-align: left; }
/* The horoscope's own pencil (PersonalHoroscope's ghost pencil). */
.horo-edit { position: absolute; top: 8px; right: -4px; width: 30px; height: 30px; border: 0; background: none; color: var(--lilac);
  font-size: 13px; cursor: pointer; opacity: .5; transition: opacity .2s, color .2s; }
.horo:hover .horo-edit, .horo-edit:hover, .horo-edit.on { opacity: 1; color: var(--goldlt); }
.horo-tune { margin-top: 14px; }
.horo .rule { height: 1px; background: linear-gradient(90deg, transparent, rgba(240,217,163,.4), transparent); margin: 0 0 18px; }
.horo .eyebrow { color: var(--lilac); text-align: center; }
.horo .horo-t { text-align: center; }
.closing { font: italic 16px var(--serif); color: var(--dim); text-align: center; margin: 18px 0 0; }
.st { opacity: 0; animation: alt-up .9s ease forwards; }
.alt.settled .st { animation: none; opacity: 1; }

/* sheets, lightbox */
.scrim { position: absolute; inset: 0; z-index: 20; background: rgba(7,2,15,.55); animation: alt-fade .3s ease both; }
.sheet { position: absolute; z-index: 21; left: 0; right: 0; bottom: 0; padding: 10px 22px 26px; border-radius: 24px 24px 0 0; text-align: left;
  background: linear-gradient(#22123f, #130a28); border-top: 1px solid rgba(240,217,163,.4); box-shadow: 0 -20px 50px rgba(0,0,0,.5);
  animation: alt-sheetup .45s cubic-bezier(.2,.8,.2,1) both; max-height: 86%; overflow-y: auto; box-sizing: border-box; }
.sheet .grab { width: 40px; height: 4px; border-radius: 2px; background: rgba(155,134,201,.5); margin: 0 auto 14px; }
.sheet .it-text { display: block; font-size: 26px; line-height: 1.2; margin: 8px 0 2px; }
.sheet .sub { font: italic 16px var(--serif); color: var(--mut); margin: 0 0 18px; }
.sheet h4 { font: 500 26px var(--serif); color: #fff; margin: 6px 0 10px; }
.sheet label { display: block; font-size: 12px; color: var(--lilac); margin: 12px 0 6px; }
.sheet input[type=range] { width: 100%; accent-color: #c9a94e; }
.sheet .ta { min-height: 64px; font-size: 18px; font-style: normal; color: var(--ink2); }
.lbx { position: absolute; inset: 0; z-index: 30; background: rgba(3,1,8,.92); display: flex; align-items: center; justify-content: center; animation: alt-fade .3s ease both; }
.lbx-img { max-width: min(92%, 640px); max-height: 86%; border-radius: 16px; border: 1px solid rgba(240,217,163,.25); }
.lbx .x { position: absolute; top: 8px; right: 14px; background: none; border: 0; color: #fff; font-size: 30px; cursor: pointer; }

/* desktop: the painting is a feathered canvas beside the words. Keyed to the
   Altar's own width, so the assistant's pane folds it to one column. */
@container (min-width: 1000px) {
  .alt.lit .amb { opacity: 1; }
  /* The painting's top sits level with the date line beside it (.hm's 128px):
     the feather reaches a quarter (about 25px into 540px) at the date's cap line. */
  .room-box { left: calc(50% - 476px); right: auto; top: 106px; width: 540px; max-width: none; height: auto; max-height: none; overflow: hidden; }
  .room-box :deep(.veil) { background: none; }
  .room-tools { top: auto; bottom: 15%; }
  .invite { left: calc(50% - 476px); width: 540px; right: auto; top: 330px; }
  .layer { max-width: 1024px; margin: 0 auto; padding: 0 56px 0 604px; box-sizing: border-box; }
  .col { max-width: none; margin: 0; }
  .col.rite { text-align: left; padding-top: 0; }
  .rite-moon { justify-content: flex-start; margin-top: 84px; }
  .offer { justify-content: flex-start; margin-top: 150px; }
  .slot { text-align: left; margin-top: 70px; }
  .hm { padding-top: 128px; text-align: left; }
  .horo-b { font-size: 21px; }
  .intent .it-text { font-size: 30px; }
  .intent-wrap .intent { padding-left: 0; }
  .horo .rule { background: linear-gradient(90deg, rgba(240,217,163,.4), transparent); }
  .horo .eyebrow, .horo .horo-t, .closing { text-align: left; }
  .painting-msg { margin-top: 220px; text-align: left; }
  .sheet { left: auto; right: 40px; bottom: auto; top: 120px; width: 380px; border-radius: 22px; border: 1px solid rgba(240,217,163,.4);
    animation: alt-rise .4s ease both; }
  .sheet .grab { display: none; }
  .hero { max-width: 440px; padding: 26px 30px 22px; }
  .col.rite :deep(.mk) { padding-bottom: 20px; }
  .col.rite :deep(.mk .eyebrow), .col.rite :deep(.mk h2), .col.rite :deep(.mk .lede) { text-align: left; }
}

/* "Make the space yours" on a tablet and up (2026-10-30): the preview stays
   still on the left while the choices scroll on the right, with the caption
   under it. 340px from 760px, 452px from 1000px. */
@container (min-width: 760px) {
  .alt.paint-step .col { --mk-head-top: 22px; }
  .alt.paint-step .room-box, .alt.choosing .room-box { left: 24px; right: auto; top: 76px; width: 340px; max-width: none; max-height: none; margin: 0; height: auto; }
  .alt.paint-step .room-box :deep(.veil) { background: none; }
  .alt.paint-step .recipe { display: block; position: absolute; z-index: 1; left: 24px; width: 340px; top: calc(76px + 340px + 6px);
    text-align: center; font: italic 17px/1.4 var(--serif); color: var(--mut); animation: alt-fade .6s ease both; }
  .alt.paint-step .layer { padding: 0 28px 0 392px; }
  .alt.paint-step .col { max-width: none; margin: 0; }
  .alt.paint-step .col.rite { text-align: left; padding-top: 20px; }
  .alt.paint-step .slot { text-align: left; margin: 56px 0 0; }
  .alt.paint-step .painting-msg { margin-top: 46px; text-align: left; }
}
@container (min-width: 1000px) {
  .alt.paint-step .room-box { left: calc(50% - 472px); width: 452px; }
  .alt.paint-step .recipe { left: calc(50% - 472px); width: 452px; top: calc(76px + 452px + 4px); }
  .alt.paint-step .layer { max-width: 1024px; margin: 0 auto; padding: 0 52px 0 532px; }
  .alt.paint-step .slot { margin-top: 70px; }
  .alt.paint-step .it-text { font-size: 23px; }
}

@keyframes alt-rise { from { opacity: 0; transform: translateY(18px) scale(.97); } to { opacity: 1; transform: none; } }
@keyframes alt-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes alt-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes alt-moonin { from { opacity: 0; transform: translateY(-14px) scale(.85); } to { opacity: 1; transform: none; } }
@keyframes alt-lnin { from { opacity: 0; filter: blur(3px); } to { opacity: 1; filter: none; } }
@keyframes alt-wordin { from { opacity: 0; filter: blur(6px); transform: translateY(6px); } to { opacity: 1; filter: none; transform: none; } }
@keyframes alt-sheetup { from { transform: translateY(100%); } to { transform: none; } }
@keyframes alt-dots { 50% { opacity: .2; } }
@keyframes alt-pulse { 50% { opacity: .3; transform: scale(.7); } }

/* Reduced motion: short crossfades only. No movement, flicker, drawing, parallax or rising. */
.rm *, .rm *::before, .rm *::after { animation-duration: .2s !important; animation-delay: 0s !important; animation-iteration-count: 1 !important; transition-duration: .2s !important; }
.rm .rite-moon :deep(svg), .rm .rite h2, .rm .st, .rm .after, .rm .hero, .rm .sheet, .rm .intent-big .w, .rm .ln { animation-name: alt-fade !important; }
.rm .painting-msg .d, .rm .pulse { animation: none !important; }
</style>

<style>
/* The kept words while they rise (appended to the Altar, outside the scoped tree). */
.altar-fly { position: absolute; z-index: 60; margin: 0; pointer-events: none; font: italic 500 33px/1.18 'Cormorant Garamond', 'Cormorant', Georgia, serif;
  color: #ffe7b5; text-shadow: 0 0 22px rgba(240,217,163,.35); transform-origin: 0 0; }
</style>
